import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, errDetail, fmtDate } from "../lib/api";
import { useApp } from "../lib/store";
import { useOpenById } from "../lib/useOpenById";
import { DataTable } from "../components/DataTable";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus, ChevronLeft, ChevronRight, CalendarDays, Table2, Phone, Users, Mail, MapPin, CheckSquare, Check } from "lucide-react";

const TYPES = ["Call", "Meeting", "E-Mail", "Visit", "Task"];
const TYPE_STYLE = {
  Call: "bg-blue-100 text-blue-800", Meeting: "bg-indigo-100 text-indigo-800",
  "E-Mail": "bg-slate-100 text-slate-600", Visit: "bg-amber-100 text-amber-800", Task: "bg-emerald-100 text-emerald-800",
};
const TYPE_ICON = { Call: Phone, Meeting: Users, "E-Mail": Mail, Visit: MapPin, Task: CheckSquare };
const PRIO_STYLE = { High: "bg-red-100 text-red-700", Medium: "bg-amber-50 text-amber-700", Low: "bg-slate-100 text-slate-500" };

export function ActivityForm({ initial, accounts, opps, onSaved, onCancel, createEndpoint }) {
  const { t } = useApp();
  const [form, setForm] = useState({ type: "Call", date: new Date().toISOString().slice(0, 10), time: "09:00", priority: "Medium", status: "Open", ...initial });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form };
      const acc = accounts.find((a) => a.id === body.account_id);
      if (acc) body.account_name = acc.name;
      const opp = opps.find((o) => o.id === body.opportunity_id);
      if (opp) body.opportunity_name = opp.name;
      if (initial.id) await api.put(`/activities/${initial.id}`, body);
      else await api.post(createEndpoint || "/activities", body);
      toast.success(t("save") + " ✓");
      onSaved();
    } catch (e) { toast.error(errDetail(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-2">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs text-gray-600">{t("activity_type")}</Label>
          <Select value={form.type} onValueChange={(v) => set("type", v)}>
            <SelectTrigger data-testid="activity-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>{TYPES.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-gray-600">{t("status")}</Label>
          <Select value={form.status} onValueChange={(v) => set("status", v)}>
            <SelectTrigger data-testid="activity-status-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="Open">{t("open_status")}</SelectItem><SelectItem value="Done">{t("done")}</SelectItem></SelectContent>
          </Select>
        </div>
        <div className="col-span-2 space-y-1">
          <Label className="text-xs text-gray-600">{t("subject")} *</Label>
          <Input data-testid="activity-subject-input" value={form.subject || ""} onChange={(e) => set("subject", e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-gray-600">{t("date")}</Label>
          <Input data-testid="activity-date-input" type="date" value={form.date || ""} onChange={(e) => set("date", e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-gray-600">{t("time")}</Label>
          <Input data-testid="activity-time-input" type="time" value={form.time || ""} onChange={(e) => set("time", e.target.value)} />
        </div>
        {form.type === "Task" && (
          <div className="space-y-1">
            <Label className="text-xs text-gray-600">{t("due_date")}</Label>
            <Input data-testid="activity-due-input" type="date" value={form.due_date || ""} onChange={(e) => set("due_date", e.target.value)} />
          </div>
        )}
        <div className="space-y-1">
          <Label className="text-xs text-gray-600">{t("priority")}</Label>
          <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
            <SelectTrigger data-testid="activity-priority-select"><SelectValue /></SelectTrigger>
            <SelectContent>{["High", "Medium", "Low"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-gray-600">{t("account")}</Label>
          <Select value={form.account_id || ""} onValueChange={(v) => set("account_id", v)}>
            <SelectTrigger data-testid="activity-account-select"><SelectValue placeholder="–" /></SelectTrigger>
            <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-gray-600">{t("opportunities")}</Label>
          <Select value={form.opportunity_id || ""} onValueChange={(v) => set("opportunity_id", v)}>
            <SelectTrigger data-testid="activity-opp-select"><SelectValue placeholder="–" /></SelectTrigger>
            <SelectContent>{opps.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="col-span-2 space-y-1">
          <Label className="text-xs text-gray-600">{t("description")}</Label>
          <Textarea data-testid="activity-description-input" rows={2} value={form.description || ""} onChange={(e) => set("description", e.target.value)} />
        </div>
      </div>
      {form.type === "Visit" && (
        <div className="border border-amber-200 bg-amber-50/50 rounded-lg p-3 space-y-3" data-testid="visit-report-section">
          <p className="text-xs font-bold uppercase tracking-widest text-amber-700">{t("visit_report")}</p>
          {[["attendees", t("attendees")], ["topics", t("topics")], ["agreements", t("agreements")], ["next_steps", t("next_steps")]].map(([k, lbl]) => (
            <div key={k} className="space-y-1">
              <Label className="text-xs text-gray-600">{lbl}</Label>
              <Textarea data-testid={`visit-${k}-input`} rows={2} className="bg-white" value={form[k] || ""} onChange={(e) => set(k, e.target.value)} />
            </div>
          ))}
        </div>
      )}
      <div className="flex justify-end gap-2 pt-2 border-t">
        <Button variant="outline" onClick={onCancel} data-testid="activity-cancel-btn">{t("cancel")}</Button>
        <Button className="bg-[#1B2A4A] hover:bg-[#2a3d63] text-white" onClick={save} disabled={saving || !form.subject} data-testid="activity-save-btn">{t("save")}</Button>
      </div>
    </div>
  );
}

export default function Activities() {
  const { t, user, canWrite } = useApp();
  const [rows, setRows] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [opps, setOpps] = useState([]);
  const [view, setView] = useState("calendar");
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [ownerFilter, setOwnerFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [params, setParams] = useSearchParams();

  const load = useCallback(() => { api.get("/activities?limit=500").then((r) => setRows(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/accounts?limit=500").then((r) => setAccounts(r.data)).catch(() => {});
    api.get("/opportunities?limit=500").then((r) => setOpps(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (params.get("new") === "1") { setEditing({}); setParams({}, { replace: true }); }
  }, [params, setParams]);
  useOpenById("activities", (doc) => { setView("table"); setEditing(doc); });

  const owners = useMemo(() => [...new Set(rows.map((r) => r.owner_name).filter(Boolean))], [rows]);
  const filtered = ownerFilter === "all" ? rows : ownerFilter === "me" ? rows.filter((r) => r.owner_id === user.id) : rows.filter((r) => r.owner_name === ownerFilter);

  const weeks = useMemo(() => {
    const first = new Date(cursor);
    const offset = (first.getDay() + 6) % 7;
    const start = new Date(first);
    start.setDate(first.getDate() - offset);
    const out = [];
    for (let w = 0; w < 6; w++) {
      const week = [];
      for (let d = 0; d < 7; d++) {
        const day = new Date(start);
        day.setDate(start.getDate() + w * 7 + d);
        week.push(day);
      }
      out.push(week);
    }
    return out;
  }, [cursor]);

  const byDate = useMemo(() => {
    const m = {};
    filtered.forEach((a) => { if (a.date) (m[a.date] = m[a.date] || []).push(a); });
    return m;
  }, [filtered]);

  const toggleDone = async (e, a) => {
    e.stopPropagation();
    try {
      await api.put(`/activities/${a.id}`, { status: a.status === "Done" ? "Open" : "Done" });
      load();
    } catch (err) { toast.error(errDetail(err)); }
  };

  const todayStr = new Date().toISOString().slice(0, 10);
  const monthLabel = cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" });

  const columns = [
    { key: "type", label: t("activity_type"), render: (r) => {
      const Icon = TYPE_ICON[r.type] || CheckSquare;
      return <Badge className={`${TYPE_STYLE[r.type] || ""} hover:bg-inherit`}><Icon className="h-3 w-3 mr-1" />{r.type}</Badge>;
    } },
    { key: "subject", label: t("subject"), render: (r) => <span className={`font-medium ${r.status === "Done" ? "line-through text-gray-400" : "text-[#1B2A4A]"}`}>{r.subject}</span> },
    { key: "date", label: t("date"), render: (r) => `${fmtDate(r.date)} ${r.time || ""}` },
    { key: "due_date", label: t("due_date"), render: (r) => r.due_date ? fmtDate(r.due_date) : "–" },
    { key: "priority", label: t("priority"), render: (r) => <Badge className={`${PRIO_STYLE[r.priority] || ""} hover:bg-inherit`}>{r.priority}</Badge> },
    { key: "account_name", label: t("account") },
    { key: "owner_name", label: t("owner") },
    { key: "status", label: t("status"), render: (r) => (
      <button data-testid={`toggle-done-${r.id}`} onClick={(e) => canWrite && toggleDone(e, r)}
        className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border transition-colors duration-150 ${
          r.status === "Done" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-white text-gray-500 border-gray-200 hover:border-emerald-300"}`}>
        <Check className="h-3 w-3" />{r.status === "Done" ? t("done") : t("mark_done")}
      </button>) },
  ];

  return (
    <div className="space-y-4" data-testid="activities-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-3xl font-bold tracking-tight text-gray-900" style={{ fontFamily: "Work Sans" }}>{t("activities")}</h1>
        <div className="flex items-center gap-2">
          <Select value={ownerFilter} onValueChange={setOwnerFilter}>
            <SelectTrigger className="w-44 h-9" data-testid="owner-filter-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("all_team")}</SelectItem>
              <SelectItem value="me">{t("my_only")}</SelectItem>
              {owners.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex rounded-md border border-gray-200 overflow-hidden">
            <button data-testid="view-calendar-btn" onClick={() => setView("calendar")}
              className={`px-3 py-1.5 text-sm flex items-center gap-1.5 transition-colors duration-150 ${view === "calendar" ? "bg-[#1B2A4A] text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}>
              <CalendarDays className="h-3.5 w-3.5" />{t("calendar")}
            </button>
            <button data-testid="view-list-btn" onClick={() => setView("list")}
              className={`px-3 py-1.5 text-sm flex items-center gap-1.5 transition-colors duration-150 ${view === "list" ? "bg-[#1B2A4A] text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}>
              <Table2 className="h-3.5 w-3.5" />{t("list_view")}
            </button>
          </div>
          {canWrite && (
            <Button data-testid="activities-new-btn" onClick={() => setEditing({})}
              className="bg-[#F5A623] hover:bg-[#e0961a] text-[#1B2A4A] font-semibold rounded-full">
              <Plus className="h-4 w-4 mr-1" strokeWidth={2.5} />{t("new")}
            </Button>
          )}
        </div>
      </div>

      {view === "calendar" ? (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="team-calendar">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <button data-testid="cal-prev-btn" className="p-1.5 rounded hover:bg-gray-100 transition-colors duration-150"
                onClick={() => setCursor((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1))}><ChevronLeft className="h-4 w-4" /></button>
              <span className="font-semibold text-gray-800 capitalize min-w-[150px] text-center" data-testid="cal-month-label">{monthLabel}</span>
              <button data-testid="cal-next-btn" className="p-1.5 rounded hover:bg-gray-100 transition-colors duration-150"
                onClick={() => setCursor((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1))}><ChevronRight className="h-4 w-4" /></button>
              <Button variant="outline" size="sm" className="h-7 text-xs" data-testid="cal-today-btn"
                onClick={() => { const d = new Date(); setCursor(new Date(d.getFullYear(), d.getMonth(), 1)); }}>{t("today")}</Button>
            </div>
            <div className="flex gap-3 text-xs">
              {TYPES.map((x) => <span key={x} className={`px-2 py-0.5 rounded-full ${TYPE_STYLE[x]}`}>{x}</span>)}
            </div>
          </div>
          <div className="grid grid-cols-7 border-b border-gray-100 text-[10px] uppercase tracking-widest text-gray-400 font-semibold">
            {["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"].map((d) => <div key={d} className="px-2 py-1.5">{d}</div>)}
          </div>
          {weeks.map((week, wi) => (
            <div key={wi} className="grid grid-cols-7 border-b border-gray-100 last:border-b-0">
              {week.map((day) => {
                const ds = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, "0")}-${String(day.getDate()).padStart(2, "0")}`;
                const inMonth = day.getMonth() === cursor.getMonth();
                const items = byDate[ds] || [];
                return (
                  <div key={ds} className={`min-h-[92px] border-r border-gray-100 last:border-r-0 p-1.5 group ${inMonth ? "" : "bg-gray-50/60"}`}
                    data-testid={`cal-day-${ds}`}>
                    <div className="flex items-center justify-between">
                      <span className={`text-xs font-mono ${ds === todayStr ? "h-5 w-5 rounded-full bg-[#F5A623] text-[#1B2A4A] font-bold flex items-center justify-center" : inMonth ? "text-gray-600" : "text-gray-300"}`}>
                        {day.getDate()}
                      </span>
                      {canWrite && (
                        <button className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-[#1B2A4A] transition-opacity duration-150"
                          data-testid={`cal-add-${ds}`} onClick={() => setEditing({ date: ds })}>
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                    <div className="space-y-0.5 mt-1">
                      {items.slice(0, 3).map((a) => (
                        <button key={a.id} data-testid="cal-activity-chip" onClick={() => setEditing(a)}
                          className={`block w-full text-left text-[10px] leading-tight px-1.5 py-0.5 rounded truncate transition-transform duration-100 hover:-translate-y-px ${TYPE_STYLE[a.type] || "bg-gray-100"} ${a.status === "Done" ? "opacity-50 line-through" : ""}`}
                          title={`${a.time || ""} ${a.subject} · ${a.owner_name}`}>
                          {a.time && <span className="font-mono">{a.time} </span>}{a.subject}
                        </button>
                      ))}
                      {items.length > 3 && <span className="text-[10px] text-gray-400 pl-1">+{items.length - 3}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      ) : (
        <DataTable module="activities" rows={filtered} columns={columns} onRowClick={setEditing} onImported={load} />
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>{editing?.id ? t("edit") : t("create")} – {t("activities")}</DialogTitle></DialogHeader>
          {editing && <ActivityForm initial={editing} accounts={accounts} opps={opps}
            onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
